import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '../api/products';
import { categoriesApi } from '../api/categories';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import {
  AvailableBadge,
  Card,
  PageHead,
} from '../components/ui';
import { StoreSelector } from '../components/StoreSelector';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import { money } from '../lib/format';
import { parseSpreadsheet, downloadTemplate } from '../lib/spreadsheet';
import type {
  Category,
  Product,
  ProductBulkResult,
  ProductCreateRequest,
} from '../types';

export function ProductsPage() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const { selectedStoreId } = useStores();
  const storeId = selectedStoreId;

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [imaging, setImaging] = useState<Product | null>(null);

  // Debounce the search box.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function onSearchChange(v: string) {
    setSearch(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(v), 300);
  }

  const categoriesQ = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list({ storeId, activeOnly: false }),
  });

  const productsQ = useQuery({
    queryKey: ['products', storeId, categoryId, debounced],
    queryFn: () =>
      productsApi.list({ storeId, categoryId, q: debounced }),
  });

  const categoryName = useMemo(() => {
    const map = new Map((categoriesQ.data ?? []).map((c) => [c.id, c.name]));
    return (id: number) => map.get(id) ?? `#${id}`;
  }, [categoriesQ.data]);

  const invalidateProducts = () =>
    qc.invalidateQueries({ queryKey: ['products'] });

  const availability = useMutation({
    mutationFn: ({ id, available }: { id: number; available: boolean }) =>
      productsApi.setAvailability(id, available),
    onSuccess: invalidateProducts,
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => productsApi.deactivate(id),
    onSuccess: invalidateProducts,
  });

  const categories = categoriesQ.data ?? [];

  return (
    <>
      <PageHead
        title="Products"
        subtitle="Manage menu items, pricing, images and availability."
        actions={
          <div className="row gap-sm">
            <button
              className="btn btn-secondary"
              onClick={() => setBulk(true)}
              disabled={categories.length === 0}
            >
              ⬆ Bulk Upload
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setCreating(true)}
              disabled={categories.length === 0}
            >
              + New Product
            </button>
          </div>
        }
      />

      <div className="toolbar">
        {isSuperAdmin && <StoreSelector allowAll label="Store" />}
        <input
          style={{ width: 240 }}
          placeholder="Search products…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <select
          style={{ width: 'auto', minWidth: 160 }}
          value={categoryId ?? ''}
          onChange={(e) =>
            setCategoryId(e.target.value === '' ? null : Number(e.target.value))
          }
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {productsQ.isLoading ? (
        <Loading />
      ) : productsQ.isError ? (
        <ErrorState
          message={toErrorMessage(productsQ.error)}
          onRetry={productsQ.refetch}
        />
      ) : !productsQ.data || productsQ.data.length === 0 ? (
        <Card>
          <Empty
            icon="🍔"
            title="No products found"
            hint={
              categories.length === 0
                ? 'Create a category first, then add products.'
                : 'Try a different search or category filter.'
            }
          />
        </Card>
      ) : (
        <div className="product-grid">
          {productsQ.data.map((p) => (
            <div className="product-card" key={p.id}>
              <div className="product-thumb">
                {p.primaryImageUrl ? (
                  <img src={p.primaryImageUrl} alt={p.name} loading="lazy" />
                ) : (
                  <span className="placeholder">🍔</span>
                )}
              </div>
              <div className="product-body">
                <div className="p-name">{p.name}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {categoryName(p.categoryId)}
                  {p.sku ? ` · ${p.sku}` : ''}
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="p-price">{money(p.price)}</span>
                  <AvailableBadge available={p.available} />
                </div>
                {!p.active && (
                  <span className="badge badge-gray">Deactivated</span>
                )}
              </div>
              <div className="product-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditing(p)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setImaging(p)}
                >
                  Image
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    availability.mutate({ id: p.id, available: !p.available })
                  }
                  disabled={availability.isPending}
                >
                  {p.available ? 'Mark unavailable' : 'Mark available'}
                </button>
                {p.active && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      if (window.confirm(`Deactivate “${p.name}”?`))
                        deactivate.mutate(p.id);
                    }}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ProductFormModal
          title="New Product"
          categories={categories}
          initial={{
            categoryId: categories[0]?.id ?? 0,
            storeId,
            name: '',
            sku: '',
            description: '',
            price: 0,
            gstRate: 5,
            gstApplicable: true,
            active: true,
            available: true,
          }}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidateProducts();
          }}
          submit={(d) => productsApi.create(d)}
        />
      )}

      {bulk && (
        <BulkUploadModal
          categories={categories}
          storeId={storeId}
          onClose={() => setBulk(false)}
          onImported={invalidateProducts}
        />
      )}

      {editing && (
        <ProductFormModal
          title={`Edit ${editing.name}`}
          categories={categories}
          initial={{
            categoryId: editing.categoryId,
            storeId: editing.storeId,
            name: editing.name,
            sku: editing.sku ?? '',
            description: editing.description ?? '',
            price: editing.price,
            gstRate: editing.gstRate,
            gstApplicable: editing.gstApplicable,
            active: editing.active,
            available: editing.available,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidateProducts();
          }}
          submit={(d) => productsApi.update(editing.id, d)}
        />
      )}

      {imaging && (
        <ImageUploadModal
          product={imaging}
          onClose={() => setImaging(null)}
          onSaved={() => {
            invalidateProducts();
          }}
        />
      )}
    </>
  );
}

function ProductFormModal({
  title,
  categories,
  initial,
  onClose,
  onSaved,
  submit,
}: {
  title: string;
  categories: Category[];
  initial: ProductCreateRequest;
  onClose: () => void;
  onSaved: () => void;
  submit: (d: ProductCreateRequest) => Promise<Product>;
}) {
  const [draft, setDraft] = useState<ProductCreateRequest>(initial);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<ProductCreateRequest>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const mutation = useMutation({
    mutationFn: () => submit(draft),
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={
              mutation.isPending ||
              !draft.name ||
              !draft.categoryId ||
              draft.price < 0
            }
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field-row">
          <div className="field">
            <label>Name *</label>
            <input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              autoFocus
              required
            />
          </div>
          <div className="field">
            <label>Category *</label>
            <select
              value={draft.categoryId}
              onChange={(e) => set({ categoryId: Number(e.target.value) })}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>SKU</label>
            <input
              value={draft.sku ?? ''}
              onChange={(e) => set({ sku: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Price (₹) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.price}
              onChange={(e) => set({ price: Number(e.target.value) })}
              required
            />
          </div>
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            value={draft.description ?? ''}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label>GST Rate (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.gstRate ?? 0}
              onChange={(e) => set({ gstRate: Number(e.target.value) })}
            />
          </div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <div className="checkbox-row" style={{ paddingBottom: 9 }}>
              <input
                id="gstApplicable"
                type="checkbox"
                checked={draft.gstApplicable ?? true}
                onChange={(e) => set({ gstApplicable: e.target.checked })}
              />
              <label htmlFor="gstApplicable">GST applicable</label>
            </div>
          </div>
        </div>
        <div className="row gap-sm" style={{ marginTop: 4 }}>
          <div className="checkbox-row">
            <input
              id="prodActive"
              type="checkbox"
              checked={draft.active ?? true}
              onChange={(e) => set({ active: e.target.checked })}
            />
            <label htmlFor="prodActive">Active</label>
          </div>
          <div className="checkbox-row" style={{ marginLeft: 20 }}>
            <input
              id="prodAvail"
              type="checkbox"
              checked={draft.available ?? true}
              onChange={(e) => set({ available: e.target.checked })}
            />
            <label htmlFor="prodAvail">Available</label>
          </div>
        </div>
        <div className="hint" style={{ marginTop: 10 }}>
          Save the product first, then use “Image” to upload photos.
        </div>
      </form>
    </Modal>
  );
}

function ImageUploadModal({
  product,
  onClose,
  onSaved,
}: {
  product: Product;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [makePrimary, setMakePrimary] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<string[]>(product.imageUrls ?? []);

  const mutation = useMutation({
    mutationFn: () => productsApi.uploadImage(product.id, file!, makePrimary),
    onSuccess: (updated) => {
      setImages(updated.imageUrls ?? []);
      setFile(null);
      setPreview(null);
      onSaved();
    },
    onError: (err) => setError(toErrorMessage(err)),
  });

  function onPick(f: File | null) {
    setError(null);
    setFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview(null);
    }
  }

  return (
    <Modal
      open
      title={`Images · ${product.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={() => mutation.mutate()}
            disabled={!file || mutation.isPending}
          >
            {mutation.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}

      {images.length > 0 && (
        <div className="field">
          <label>Current images</label>
          <div className="row gap-sm flex-wrap">
            {images.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border:
                    url === product.primaryImageUrl
                      ? '2px solid var(--red)'
                      : '1px solid var(--line)',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="field">
        <label>Upload new image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
      </div>

      <div className="img-preview">
        {preview ? (
          <img src={preview} alt="preview" />
        ) : (
          <span className="text-muted">Preview will appear here</span>
        )}
      </div>

      <div className="checkbox-row" style={{ marginTop: 12 }}>
        <input
          id="makePrimary"
          type="checkbox"
          checked={makePrimary}
          onChange={(e) => setMakePrimary(e.target.checked)}
        />
        <label htmlFor="makePrimary">Set as primary image</label>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------- bulk product upload
function normKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function parseBool(v: string, dflt: boolean): boolean {
  const s = v.trim().toLowerCase();
  if (!s) return dflt;
  if (['yes', 'y', 'true', '1', 'available', 'active'].includes(s)) return true;
  if (['no', 'n', 'false', '0', 'unavailable', 'inactive'].includes(s)) return false;
  return dflt;
}

interface ParsedRow {
  index: number; // 1-based data row number (excludes the header)
  name: string;
  category: string;
  price: string;
  draft?: ProductCreateRequest;
  error?: string;
}

function mapRow(
  raw: Record<string, string>,
  index: number,
  catByName: Map<string, number>,
  storeId: number | null,
): ParsedRow {
  const map: Record<string, string> = {};
  Object.keys(raw).forEach((k) => {
    map[normKey(k)] = String(raw[k] ?? '').trim();
  });
  const get = (...keys: string[]) => {
    for (const k of keys) {
      const v = map[normKey(k)];
      if (v) return v;
    }
    return '';
  };

  const name = get('name');
  const category = get('category', 'categoryname');
  const priceStr = get('price');
  const base = { index, name, category, price: priceStr };

  if (!name) return { ...base, error: 'Missing Name' };
  if (!category) return { ...base, error: 'Missing Category' };
  const categoryId = catByName.get(normKey(category));
  if (!categoryId) return { ...base, error: `Unknown category "${category}"` };
  const price = Number(priceStr);
  if (!priceStr || Number.isNaN(price) || price < 0)
    return { ...base, error: `Invalid Price "${priceStr}"` };

  const gstRateStr = get('gstrate', 'gstratepercent');
  const gstRate = gstRateStr ? Number(gstRateStr) : 5;
  if (Number.isNaN(gstRate) || gstRate < 0)
    return { ...base, error: `Invalid GST Rate "${gstRateStr}"` };

  const draft: ProductCreateRequest = {
    categoryId,
    storeId: storeId ?? undefined,
    name,
    sku: get('sku') || undefined,
    description: get('description', 'desc') || undefined,
    price,
    gstRate,
    gstApplicable: parseBool(get('gstapplicable', 'gst'), true),
    available: parseBool(get('available'), true),
    active: true,
  };
  return { ...base, draft };
}

function BulkUploadModal({
  categories,
  storeId,
  onClose,
  onImported,
}: {
  categories: Category[];
  storeId: number | null;
  onClose: () => void;
  onImported: () => void;
}) {
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProductBulkResult | null>(null);

  const catByName = useMemo(() => {
    const m = new Map<string, number>();
    categories.forEach((c) => m.set(normKey(c.name), c.id));
    return m;
  }, [categories]);

  const valid = rows?.filter((r) => r.draft) ?? [];
  const invalid = rows?.filter((r) => r.error) ?? [];

  async function onPick(file: File | null) {
    setError(null);
    setResult(null);
    setRows(null);
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    try {
      const raw = await parseSpreadsheet(file);
      if (raw.length === 0) {
        setError('No rows found. Make sure the first row has the column headers.');
        return;
      }
      setRows(raw.map((r, i) => mapRow(r, i + 1, catByName, storeId)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setParsing(false);
    }
  }

  const importMut = useMutation({
    mutationFn: () => productsApi.bulkCreate(valid.map((r) => r.draft!)),
    onSuccess: (res) => {
      setResult(res);
      onImported();
    },
    onError: (e) => setError(toErrorMessage(e)),
  });

  return (
    <Modal
      open
      wide
      title="Bulk Upload Products"
      onClose={onClose}
      footer={
        result ? (
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => importMut.mutate()}
              disabled={valid.length === 0 || importMut.isPending}
            >
              {importMut.isPending
                ? 'Importing…'
                : `Import ${valid.length} product${valid.length === 1 ? '' : 's'}`}
            </button>
          </>
        )
      }
    >
      {error && <div className="form-error">{error}</div>}

      {result ? (
        <div>
          <div
            className="badge badge-green"
            style={{ fontSize: 14, padding: '6px 10px' }}
          >
            ✓ Created {result.created} product{result.created === 1 ? '' : 's'}
          </div>
          {result.failed > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="text-muted" style={{ marginBottom: 6 }}>
                {result.failed} row{result.failed === 1 ? '' : 's'} failed on the server:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.errors.map((e) => (
                  <li key={e.row}>
                    Row {e.row} {e.name ? `(${e.name})` : ''} — {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="hint" style={{ marginBottom: 10 }}>
            Upload an Excel (.xlsx) or CSV file. Required columns:{' '}
            <b>Name</b>, <b>Category</b> (must match an existing category name), and{' '}
            <b>Price</b>. Optional: SKU, Description, GST Rate %, GST Applicable,
            Available. Products import into the currently selected store.
          </div>

          <div className="row gap-sm" style={{ marginBottom: 12 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => downloadTemplate(categories[0]?.name)}
            >
              ⬇ Download template
            </button>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              {fileName ? `📄 ${fileName}` : '📂 Choose file…'}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => onPick(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          {parsing && <div className="text-muted">Reading file…</div>}

          {rows && (
            <>
              <div style={{ marginBottom: 8 }}>
                <span className="badge badge-green">{valid.length} ready</span>{' '}
                {invalid.length > 0 && (
                  <span className="badge badge-red">
                    {invalid.length} with problems (skipped)
                  </span>
                )}
              </div>
              <div
                style={{
                  maxHeight: 320,
                  overflow: 'auto',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                }}
              >
                <table className="data" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th style={{ textAlign: 'right' }}>Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.index}>
                        <td className="text-muted">{r.index}</td>
                        <td>{r.name || <span className="text-muted">—</span>}</td>
                        <td>{r.category || <span className="text-muted">—</span>}</td>
                        <td style={{ textAlign: 'right' }}>{r.price}</td>
                        <td>
                          {r.error ? (
                            <span style={{ color: 'var(--red)' }}>{r.error}</span>
                          ) : (
                            <span style={{ color: 'var(--green, green)' }}>OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
